import styled from '@emotion/styled';
import type { MouseEvent } from 'react';

import BayAvatar from '../../BayAvatar';
import BayTooltip from '../../BayTooltip';
import DollarIcon from '../../Icons/DollarIcon';
import { DividerDiv } from '../styles/navStyles';

import BrandLogo from './BrandLogo';
import getProfileImageUrl from './getProfileImageUrl';
import isActingAsAnotherUser from './isActingAsAnotherUser';
import isEnterpriseBroker from './isEnterpriseBroker';

import { BsPersonBoundingBox } from 'react-icons/bs';

import type { TranslationFunction } from '../../types/common';
import type { BooleanCallback } from '../../types/callbacks';

interface User {
	ActingAsPLATFORMLOGOPATH?: string;
	ActingAsFIRSTNAME?: string;
	ActingAsIMAGEPATH?: string;
	ActingAsIMAGE_DATA?: string;
	ActingAsLASTNAME?: string;
	isEnterprise?: boolean;
	isEnterpriseChild?: boolean;
	type?: string;
	showActAsButton?: boolean;
	agent_charges_to_parent_account?: boolean;
	totalCredits?: string | number;
}

interface MyAccountButtonProps {
	user?: User;
	toggleFunc: () => void;
	showActAsModal?: BooleanCallback;
	platform?: string;
	t?: TranslationFunction;
}

const StyledButton = styled.button<{ hasLogo?: boolean }>`
	display: flex;
	align-items: center;
	align-content: center;
	justify-content: center;
	padding: ${(props) => (props.hasLogo ? '0 0.4rem 0 0' : '0 0.4rem')};
	text-decoration: none;
	transition:
		color 0.15s ease-in-out,
		background-color 0.15s ease-in-out,
		border-color 0.15s ease-in-out;
	border: 0;
	border-radius: 0.25rem;
	min-height: 2.85rem;
	min-width: 2.85rem;

	@media only screen and (min-width: 992px) {
		img.dropdown-user-img {
			margin-right: 0.5rem;
		}
	}
`;

const ActAs = styled.div`
	padding: 0.3rem 0.5rem 0.4rem 0.5rem;
	color: var(--bs-gray-700);

	&:hover,
	&:focus-visible {
		background-color: var(--bs-primary-bg-subtle, #cfe2ff);
		border-color: var(--bs-primary-bg-subtle, #cfe2ff);
		color: var(--bs-primary);
	}
`;

const MyAccountButton = ({
	user,
	toggleFunc,
	showActAsModal = () => {},
	platform = '',
	t = (key) => key,
}: MyAccountButtonProps) => {
	const topNavImageUrl = getProfileImageUrl(
		user?.ActingAsIMAGE_DATA,
		user?.ActingAsIMAGEPATH,
	);

	const handleActAsClick = (e: MouseEvent<HTMLDivElement>) => {
		e.stopPropagation();
		showActAsModal(true);
	};

	// On Social, "Act As" is limited to Enterprise Broker accounts — every
	// Enterprise Broker variant (Parent, Child, Child Broker), but never Agent
	// accounts regardless of plan. The `showActAsButton` clause below still
	// enforces the backend business rules (e.g. an enterprise child broker must
	// be a network-group owner), so gating on isEnterpriseBroker widens the
	// account-type/plan check without loosening the permission itself. The
	// client platform keeps its existing broader rule (any permitted account).
	// For qualifying accounts, keep the existing behavior — show when permitted
	// or already acting as someone else — and hide it entirely for everyone else.
	const canActAs =
		(platform === 'client' || isEnterpriseBroker(user)) &&
		(user?.showActAsButton || isActingAsAnotherUser(user));

	return (
		<>
			<StyledButton
				className="user-dropdown-button"
				hasLogo={user?.ActingAsPLATFORMLOGOPATH}
				role="button"
				onClick={toggleFunc}
			>
				{(platform === 'client' || platform === 'social') && canActAs ? (
					<BayTooltip overlay="Act As" placement="bottom">
						<div className="actas-button d-flex align-items-center justify-content-between shadow-none">
							<ActAs
								id="act_as_button"
								className="rounded-1 border-0"
								onClick={handleActAsClick}
							>
								<BsPersonBoundingBox size={16} />
							</ActAs>
						</div>
					</BayTooltip>
				) : null}
				<BayTooltip overlay={<span>{t('account')}</span>} placement="bottomRight">
					<div className="d-flex align-items-center">
						{user?.ActingAsPLATFORMLOGOPATH && (
							<>
								<BrandLogo logoUrl={user?.ActingAsPLATFORMLOGOPATH} />

								<DividerDiv
									className="bay-nav-divider d-block mx-2"
									showDivider={
										user?.ActingAsPLATFORMLOGOPATH &&
										user?.ActingAsPLATFORMLOGOPATH?.length > 0
									}
								/>
							</>
						)}
						<BayAvatar
							firstName={user?.ActingAsFIRSTNAME}
							imgUrl={topNavImageUrl}
							lastName={user?.ActingAsLASTNAME}
							size="sm"
						/>
					</div>
				</BayTooltip>
			</StyledButton>
			{user?.isEnterprise &&
				!user?.agent_charges_to_parent_account &&
				Number(user?.totalCredits) > 0 && (
				<span className="credits-badge">
					<DollarIcon />
				</span>
			)}
		</>
	);
};

export default MyAccountButton;
